import React, { useState } from 'react';
import { message, Icon } from 'antd';
import { useMutation } from '@apollo/client';

import PropertyDetailWrapper from '~components/property-detail-wrapper';
import { onlyViewPropertyRole } from '~client/helpers/auth';
import PriceCalcDisplaySelect from 'common/components/listing-management/price-calc-display/price-calc-display-select';
import useModal from '~hooks/modal';

import DailyPriceCalcEditor from './daily-price-calc-editor';
import FinalPriceCalcEditor from './final-price-calc-editor';
import { CREATE_PRICE_CALC, UPDATE_PRICE_CALC } from './queries';
import { DailyPriceTypes, CustomLengthValueEnum } from 'common/constants/price-calc-display';
import {
  getMoveCategory,
  getMoveType,
  getMoveDate,
} from 'common/components/listing-management/price-calc-display/helper';

export const DailyPriceCalculationOptions = {
  [DailyPriceTypes.CUSTOM_LENGTH]: {
    label:
      'cms.property.listing_management.price-calc-display.daily_price_type.custom_length.label',
    des: 'cms.property.listing_management.price-calc-display.daily_price_type.custom_length.des',
    icon: <Icon type="calculator" />,

    extraDes: [
      ({ editingPriceCalculationRule }) =>
        `Days in a month: ${editingPriceCalculationRule.customLength}`,
      ({ editingPriceCalculationRule }) =>
        `Daily price = monthly price / ${editingPriceCalculationRule.customLength}`,
    ],
  },
  [DailyPriceTypes.CALENDAR_MONTH_DAYS]: {
    label:
      'cms.property.listing_management.price-calc-display.daily_price_type.calendar_month_days.label',
    des: 'cms.property.listing_management.price-calc-display.daily_price_type.calendar_month_days.des',
    icon: <Icon type="calendar" />,
    extraDes: [
      'cms.property.listing_management.price-calc-display.daily_price_type.calendar_month_days.extra_des.1',
    ],
  },
  CONVERTED_WEEKLY: {
    label:
      'cms.property.listing_management.price-calc-display.daily_price_type.converted_weekly.label',
    des: 'cms.property.listing_management.price-calc-display.daily_price_type.converted_weekly.des',
    icon: <Icon type="hourglass" />,
    extraDes: [],
  },
  CONVERTED_ANNUAL: {
    label:
      'cms.property.listing_management.price-calc-display.daily_price_type.converted_annual.label',
    des: 'cms.property.listing_management.price-calc-display.daily_price_type.converted_annual.des',
    icon: <Icon type="interaction" />,
    extraDes: [
      ({ editingPriceCalculationRule }) =>
        `Days in a year: ${editingPriceCalculationRule.customLength}`,
      ({ editingPriceCalculationRule }) =>
        `Daily price = monthly price*12 / ${editingPriceCalculationRule.customLength}`,
    ],
  },
};

export const FinalPriceCalculationOptions = {
  BASIC_SETTING: {
    label:
      'cms.property.listing_management.price-calc-display.final_price_calculation_option.basic.label',
    des: 'cms.property.listing_management.price-calc-display.final_price_calculation_option.basic.des',
    icon: <Icon type="money-collect" />,
    extraDes: [
      'cms.property.listing_management.price-calc-display.final_price_calculation_option.basic.extra_des.1',
    ],
  },
  ADVANCED_SETTING: {
    label:
      'cms.property.listing_management.price-calc-display.final_price_calculation_option.advanced.label',
    des: 'cms.property.listing_management.price-calc-display.final_price_calculation_option.advanced.des',
    icon: <Icon type="account-book" />,
    extraDes: [
      [
        ({ editingPriceCalculationRule }) =>
          getMoveCategory('moveIn', editingPriceCalculationRule.moveInType),
        ({ editingPriceCalculationRule }) =>
          getMoveType('moveIn', editingPriceCalculationRule.moveInType),
        ({ editingPriceCalculationRule }) =>
          getMoveDate(
            editingPriceCalculationRule.moveInType,
            editingPriceCalculationRule.moveInStart,
            editingPriceCalculationRule.moveInEnd,
          ),
      ],

      [
        ({ editingPriceCalculationRule }) =>
          getMoveCategory('moveOut', editingPriceCalculationRule.moveOutType),
        ({ editingPriceCalculationRule }) =>
          getMoveType('moveOut', editingPriceCalculationRule.moveOutType),
        ({ editingPriceCalculationRule }) =>
          getMoveDate(
            editingPriceCalculationRule.moveOutType,
            editingPriceCalculationRule.moveOutStart,
            editingPriceCalculationRule.moveOutEnd,
          ),
      ],

      [
        'cms.property.listing_management.price-calc-display.final_price_calculation_option.advanced.extra_des.7',
      ],
    ],
    extraDesArea: [
      'A month is defined as a full calendar month i.e. If the stay is for all 31 days in Jan, all 28/29 days in Feb, all 31 days in Mar, etc',
      '<b>Charge the remaining days as a full month</b> means no matter how many days the student stays in the corresponding month, charge the remaining days as a full monthly price. These advanced settings decide how to calculate the price of the days that are not part of a full calendar month:',
      '<div><ul><li>Move in earlier than the date</li><li>Move out later than the date</li><li>Move in/out within a date range</li><li>Move in/out no limit</li></ul></div>',
      '<b>Charge the remaining days according to actual days of occupancy in the corresponding month</b> means when calculating the price for the remaining days, get the daily price from the daily price configuration and then use the daily price times remaining days.',
    ],
  },
};

export function PriceCalcDisplay({ t, property, afterSave, updateChangedState }) {
  const { id: propertyId, priceCalculationRuleSetting, billingCycle } = property;
  const useMonthly = billingCycle === 'MONTHLY';
  const defaultPriceCalculationRuleTemplate = useMonthly
    ? priceCalculationRuleSetting.allBillingCycleRule.monthly
    : priceCalculationRuleSetting.allBillingCycleRule.weekly;

  const {
    opened: showDailyPriceCalcEditor,
    open: openDailyPriceCalcEditor,
    close: closeDailyPriceCalcEditor,
  } = useModal(false);

  const {
    opened: showFinalPriceCalcEditor,
    open: openFinalPriceCalcEditor,
    close: closeFinalPriceCalcEditor,
  } = useModal(false);

  const [editingPriceCalculationRule, setEditingPriceCalculationRule] = useState(() => {
    if (priceCalculationRuleSetting?.priceCalculationRule) {
      return priceCalculationRuleSetting.priceCalculationRule;
    } else {
      return {
        calculateMethod: 'BASIC',
        ...defaultPriceCalculationRuleTemplate,
      };
    }
  });

  const [isHaveUnsavedChange, setIsHaveUnsavedChange] = useState({
    dailyPrice: false,
    finalPrice: false,
  });

  const [handleSubmit] = useMutation(
    editingPriceCalculationRule?.id ? UPDATE_PRICE_CALC : CREATE_PRICE_CALC,
  );

  function handleDailyPriceCalculationChange() {
    openDailyPriceCalcEditor(true);
  }

  function handleFinalPriceCalculationChange() {
    openFinalPriceCalcEditor(true);
  }

  function save(priceCalculationRule) {
    try {
      const newPriceCalculationRuleSetting = Object.assign(
        {},
        editingPriceCalculationRule,
        priceCalculationRule,
      );

      setEditingPriceCalculationRule(newPriceCalculationRuleSetting);
    } catch (error) {
      // callback({ status: 'err', err: error });
    }
  }

  function handleCloseDailyPriceCalcEditor({ priceCalculationRule }) {
    if (priceCalculationRule) {
      save(priceCalculationRule);
      setIsHaveUnsavedChange(prev => ({ ...prev, dailyPrice: true }));
      updateChangedState(true);
    }
    closeDailyPriceCalcEditor();
  }

  function handleCloseFinalPriceCalcEditor({ priceCalculationRule }) {
    if (priceCalculationRule) {
      save(priceCalculationRule);
      setIsHaveUnsavedChange(prev => ({ ...prev, finalPrice: true }));
      updateChangedState(true);
    }
    closeFinalPriceCalcEditor();
  }

  async function handleSave(callback = () => {}) {
    try {
      const mutationVariables = {
        ...(editingPriceCalculationRule?.id
          ? { id: editingPriceCalculationRule?.id }
          : { propertyId }),
        // DailyPriceCalcEditor values
        dailyPriceType: editingPriceCalculationRule?.dailyPriceType,
        customLength: CustomLengthValueEnum[editingPriceCalculationRule?.customLength],
        // FinalPriceCalcEditor values
        calculateMethod: editingPriceCalculationRule?.calculateMethod,
        moveInType: editingPriceCalculationRule?.moveInType,
        moveOutType: editingPriceCalculationRule?.moveOutType,
        moveInStart: editingPriceCalculationRule?.moveInStart,
        moveInEnd: editingPriceCalculationRule?.moveInEnd,
        moveOutStart: editingPriceCalculationRule?.moveOutStart,
        moveOutEnd: editingPriceCalculationRule?.moveOutEnd,
      };
      const mutationResult = await handleSubmit({
        variables: {
          input: mutationVariables,
        },
      });

      const successMessage = editingPriceCalculationRule?.id
        ? t('cms.property.listing_management.price-calc-display.update.success')
        : t('cms.property.listing_management.price-calc-display.create.success');

      message.success(successMessage);

      const newestPriceCalculationRule = editingPriceCalculationRule?.id
        ? mutationResult.data.updatePriceCalculationRule.priceCalculationRule
        : mutationResult.data.createPriceCalculationRule.priceCalculationRule;

      const newPriceCalculationRuleSetting = Object.assign(
        {},
        property.priceCalculationRuleSetting,
        { priceCalculationRule: newestPriceCalculationRule },
      );

      afterSave({ ...property, priceCalculationRuleSetting: newPriceCalculationRuleSetting });

      setIsHaveUnsavedChange({
        dailyPrice: false,
        finalPrice: false,
      });
      updateChangedState(false);

      callback({ status: 'success', isUpdated: true });
    } catch (error) {
      callback({ status: 'err', err: error });
    }
  }

  return (
    <PropertyDetailWrapper
      property={property}
      t={t}
      isPublished={property.status === 'PUBLISHED'}
      onClickSave={handleSave}
      canEdit={!onlyViewPropertyRole()}
    >
      <div className="price-calc-display">
        <div className="price-calc-display__setting-block">
          <h2 className="property-detail__subtitle">
            {t('cms.property.listing_management.price-calc-display.daily_price_calc_title')}
          </h2>
          <p className="price-calc-display__setting-block__des">
            {t('cms.property.listing_management.price-calc-display.daily_price_calc_des')}
          </p>

          <PriceCalcDisplaySelect.SelectedOption
            key="daily-selected-option"
            option={
              DailyPriceCalculationOptions[
                editingPriceCalculationRule?.dailyPriceType || DailyPriceTypes.CALENDAR_MONTH_DAYS
              ]
            }
            onChange={onlyViewPropertyRole() ? null : handleDailyPriceCalculationChange}
            editingPriceCalculationRule={editingPriceCalculationRule}
            t={t}
          />

          <UnsavedTip
            show={isHaveUnsavedChange.dailyPrice}
            t={t}
            isPublished={property.status === 'PUBLISHED'}
          />

          <PriceCalcDisplaySelect.OtherOptions
            key="daily-other-option"
            options={Object.keys(DailyPriceTypes)
              .filter(o => o !== editingPriceCalculationRule?.dailyPriceType)
              .map(o => DailyPriceCalculationOptions[o])}
            t={t}
          />
        </div>

        <div className="price-calc-display__setting-block">
          <h2 className="property-detail__subtitle">
            {t('cms.property.listing_management.price-calc-display.final_price_calc_title', {
              cycle: 'Monthly',
            })}
          </h2>
          <p className="price-calc-display__setting-block__des">
            {t('cms.property.listing_management.price-calc-display.final_price_calc_des')}
          </p>

          <PriceCalcDisplaySelect.SelectedOption
            key="final-selected-option"
            displayExtraDesArea
            option={
              FinalPriceCalculationOptions[
                editingPriceCalculationRule?.calculateMethod
                  ? `${editingPriceCalculationRule.calculateMethod}_SETTING`
                  : 'BASIC_SETTING'
              ]
            }
            onChange={onlyViewPropertyRole() ? null : handleFinalPriceCalculationChange}
            editingPriceCalculationRule={editingPriceCalculationRule}
            t={t}
          />

          <UnsavedTip
            show={isHaveUnsavedChange.finalPrice}
            t={t}
            isPublished={property.status === 'PUBLISHED'}
          />

          <PriceCalcDisplaySelect.OtherOptions
            key="final-other-option"
            displayExtraDesArea
            options={Object.keys(FinalPriceCalculationOptions)
              .filter(
                o => o.replace('_SETTING', '') !== editingPriceCalculationRule?.calculateMethod,
              )
              .map(o => FinalPriceCalculationOptions[o])}
            t={t}
          />
        </div>

        {showDailyPriceCalcEditor && (
          <DailyPriceCalcEditor
            activeModal
            propertyId={propertyId}
            editingPriceCalculationRule={editingPriceCalculationRule}
            handleCloseModal={handleCloseDailyPriceCalcEditor}
          />
        )}
        {showFinalPriceCalcEditor && (
          <FinalPriceCalcEditor
            activeModal
            propertyId={propertyId}
            editingPriceCalculationRule={editingPriceCalculationRule}
            handleCloseModal={handleCloseFinalPriceCalcEditor}
          />
        )}
      </div>
    </PropertyDetailWrapper>
  );
}

function UnsavedTip({ show, t, isPublished }) {
  if (!show) return null;
  return (
    <div className="price-calc-display__flex-align-center price-calc-display__unsaved-tip">
      <Icon type="info-circle" />
      {isPublished
        ? t('cms.property.listing_management.price-calc-display.unsaved.published.alert')
        : t('cms.property.listing_management.price-calc-display.unsaved.unpublish.alert')}
    </div>
  );
}
